<template>
  <form class="bank-account-editor" @submit.prevent="submit">
    <div class="bank-account-editor__grid">
      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-bank-name">Banco</label>
        <input
          id="bank-account-bank-name"
          v-model.trim="form.bank_name"
          class="bank-account-editor__input"
          type="text"
          placeholder="Banco de Chile, BCI..."
        >
      </div>

      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-type">Tipo de cuenta</label>
        <select
          id="bank-account-type"
          v-model="form.account_type"
          class="bank-account-editor__input"
        >
          <option disabled value="">Seleccione un tipo</option>
          <option v-for="option in accountTypes" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-number">Numero de cuenta</label>
        <input
          id="bank-account-number"
          v-model.trim="form.account_number"
          class="bank-account-editor__input"
          type="text"
          placeholder="1234567890"
        >
      </div>

      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-email">Email de pago</label>
        <input
          id="bank-account-email"
          v-model.trim="form.payment_email"
          class="bank-account-editor__input"
          type="email"
          placeholder="pagos@empresa.cl"
        >
      </div>

      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-holder">Titular</label>
        <input
          id="bank-account-holder"
          v-model.trim="form.account_holder"
          class="bank-account-editor__input"
          type="text"
          placeholder="Razon social o persona titular"
        >
      </div>

      <div class="bank-account-editor__field">
        <label class="bank-account-editor__label" for="bank-account-holder-rut">RUT titular</label>
        <input
          id="bank-account-holder-rut"
          :value="form.account_holder_rut"
          class="bank-account-editor__input"
          type="text"
          placeholder="76.123.456-7"
          @input="handleAccountHolderRutInput"
        >
      </div>

      <div class="bank-account-editor__field bank-account-editor__field--full">
        <label class="bank-account-editor__label" for="bank-account-notes">Observaciones</label>
        <textarea
          id="bank-account-notes"
          v-model.trim="form.notes"
          class="bank-account-editor__textarea"
          rows="4"
          placeholder="Datos adicionales para facturacion o tesoreria"
        ></textarea>
      </div>

      <label class="bank-account-editor__checkbox">
        <input v-model="form.is_primary" type="checkbox">
        <span>Marcar como cuenta principal</span>
      </label>
    </div>

    <p v-if="error" class="bank-account-editor__error">{{ error }}</p>

    <footer class="bank-account-editor__actions">
      <button class="bank-account-editor__ghost" type="button" :disabled="saving" @click="$emit('cancel')">
        Cancelar
      </button>

      <button class="bank-account-editor__primary" type="submit" :disabled="saving">
        {{ saving ? 'Guardando...' : submitLabel }}
      </button>
    </footer>
  </form>
</template>

<script>
import companyOperationsService from '@/services/company/companyOperations.service'
import { formatRut, splitRut } from '@/utils/rut'

const defaultForm = () => ({
  id: null,
  bank_name: '',
  account_type: '',
  account_number: '',
  account_holder: '',
  account_holder_rut: '',
  payment_email: '',
  notes: '',
  is_primary: false
})

export default {
  name: 'BankAccountEditor',
  props: {
    value: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      form: defaultForm(),
      saving: false,
      error: '',
      accountTypes: ['Cuenta Corriente', 'Cuenta Vista', 'Cuenta de Ahorro']
    }
  },
  computed: {
    submitLabel () {
      return this.form.id ? 'Guardar cambios' : 'Crear cuenta'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        this.form = {
          ...defaultForm(),
          ...(newValue || {}),
          account_holder_rut: this.formatAccountHolderRut(newValue && newValue.account_holder_rut),
          is_primary: Boolean(newValue && newValue.is_primary)
        }
      }
    }
  },
  methods: {
    formatAccountHolderRut (value) {
      const { rut, dv } = splitRut(value || '')
      return formatRut(rut, dv)
    },
    handleAccountHolderRutInput (event) {
      this.form.account_holder_rut = this.formatAccountHolderRut(event.target.value)
    },
    validate () {
      if (!this.form.bank_name) {
        this.error = 'El banco es obligatorio.'
        return false
      }
      if (!this.form.account_type) {
        this.error = 'Debes seleccionar un tipo de cuenta.'
        return false
      }
      if (!this.form.account_number) {
        this.error = 'El numero de cuenta es obligatorio.'
        return false
      }
      if (!this.form.account_holder) {
        this.error = 'El titular es obligatorio.'
        return false
      }
      this.error = ''
      return true
    },
    normalizePayload () {
      return {
        id: this.form.id,
        company_id: Number(this.companyId),
        bank_name: this.form.bank_name,
        account_type: this.form.account_type,
        account_number: this.form.account_number,
        account_holder: this.form.account_holder,
        account_holder_rut: this.form.account_holder_rut,
        payment_email: this.form.payment_email,
        notes: this.form.notes,
        is_primary: Boolean(this.form.is_primary)
      }
    },
    async submit () {
      if (!this.validate()) return

      this.saving = true
      this.error = ''

      try {
        const payload = this.normalizePayload()
        const saved = this.form.id
          ? await companyOperationsService.updateBankAccount(payload)
          : await companyOperationsService.createBankAccount(payload)
        this.$emit('saved', saved)
      } catch (err) {
        this.error = err?.message || 'No fue posible guardar la cuenta bancaria.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.bank-account-editor {
  display: grid;
  gap: 1rem;
}

.bank-account-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.bank-account-editor__field {
  display: grid;
  gap: 0.38rem;
}

.bank-account-editor__field--full {
  grid-column: span 2;
}

.bank-account-editor__label {
  color: #4b3b2f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.bank-account-editor__input,
.bank-account-editor__textarea {
  min-height: 2.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(102, 81, 57, 0.14);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.76);
  color: #241c16;
}

.bank-account-editor__textarea {
  min-height: 7.2rem;
  resize: vertical;
}

.bank-account-editor__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #392d22;
  font-size: 0.92rem;
}

.bank-account-editor__error {
  margin: 0;
  color: #a24444;
  font-size: 0.9rem;
}

.bank-account-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
}

.bank-account-editor__ghost,
.bank-account-editor__primary {
  min-height: 2.65rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.bank-account-editor__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.bank-account-editor__primary {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

@media (max-width: 640px) {
  .bank-account-editor__grid {
    grid-template-columns: 1fr;
  }

  .bank-account-editor__field--full {
    grid-column: span 1;
  }

  .bank-account-editor__actions {
    flex-direction: column;
  }
}
</style>
